// Login, Dashboard, Audit Log, Settings screens.

import { useState, useEffect, useRef } from 'react';
import { I } from '../components/icons';
import {
  Logo, Avatar, ResourceBar, Sparkline, StatusBadge, StatusDot, Empty, Modal, KVrow, useToast,
} from '../components/ui';
import { PLANS } from '../data/mock';
import { useOperatorMode } from '../hooks/useOperatorMode';
import { onVisible } from '../hooks/onVisible';
import { updateSettings, fetchDashboard, fetchTenants, fetchAudit, fetchNodes, fetchNodeMetrics, mergeNodeMetrics, fetchNodePods, fetchMetricsSummary, fetchDeletedTenants, recoverTenant, purgeTenant, purgeAllDeleted, reloadCharts, fetchEvents, fetchPlanLimits, updatePlanLimits, setNodeGPUSlots, KnownPlans, login as apiLogin, ApiError, type DashboardSummary, type AuditEvent, type NodeStatus, type PodInfo, type MetricsSummary, type ClusterEvent, type PlanLimits } from '../api/client';
import type { AppState, AuthUser, Client, Plan, Route } from '../types';

// ─── LOGIN ──────────────────────────────────────────────────────────────────
export function LoginScreen({ onLogin }: { onLogin: (u: AuthUser) => void }) {
  const [email, setEmail] = useState('admin@shoptefy.io');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!email.trim()) { setErr('Enter your email.'); return; }
    if (!password) { setErr('Enter your password.'); return; }
    setLoading(true);
    try {
      const me = await apiLogin(email, password);
      onLogin({ email: me.email, name: me.displayName || me.email });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-card card card-pad-lg">
        <div className="login-brand">
          <Logo size={28}/>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15, letterSpacing: '-.01em' }}>Shoptefy</div>
            <div style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.12em', marginTop: -2 }}>Master Admin</div>
          </div>
        </div>
        <h1>Sign in to your console</h1>
        <p className="sub">Manage clients, cluster resources and global services.</p>

        <form onSubmit={submit} className="col" style={{ gap: 14 }}>
          <div className="field">
            <label className="field-label">Email</label>
            <div style={{ position: 'relative' }}>
              <I.Mail size={14} style={{ position: 'absolute', left: 11, top: 10, color: 'var(--text-3)' }}/>
              <input className="input" style={{ paddingLeft: 34 }} type="email"
                     value={email} onChange={(e) => setEmail(e.target.value)}
                     autoComplete="email"/>
            </div>
          </div>
          <div className="field">
            <label className="field-label">Password</label>
            <div style={{ position: 'relative' }}>
              <I.Lock size={14} style={{ position: 'absolute', left: 11, top: 10, color: 'var(--text-3)' }}/>
              <input className="input" style={{ paddingLeft: 34, paddingRight: 36 }}
                     type={showPw ? 'text' : 'password'} value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     autoComplete="current-password"
                     placeholder="••••••••••"/>
              <button type="button" className="btn btn-ghost btn-icon btn-sm"
                      style={{ position: 'absolute', right: 3, top: 3 }}
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPw(!showPw)}>
                {showPw ? <I.EyeOff size={14}/> : <I.Eye size={14}/>}
              </button>
            </div>
          </div>
          {err && <div className="field-error">{err}</div>}
          <button className="btn btn-primary btn-lg" type="submit" disabled={loading}
                  style={{ marginTop: 6 }}>
            {loading ? <><span className="spinner"/> Signing in…</> : <>Sign in <I.ArrowR size={14}/></>}
          </button>
        </form>

        <div className="divider"></div>
        <a href="/services-lookup" className="row" style={{ gap: 6, fontSize: 11.5, color: 'var(--text-3)', width: 'fit-content' }}>
          <I.Layers size={11}/> Client service lookup
        </a>
        <div className="divider"></div>
        <div className="row" style={{ justifyContent: 'center', fontSize: 11.5, color: 'var(--text-3)' }}>
          <span>Shoptefy Master Admin</span>
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD ──────────────────────────────────────────────────────────────
function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function Dashboard({ state, setState, onNav }: { state: AppState; setState: React.Dispatch<React.SetStateAction<AppState>>; onNav: (r: Route) => void }) {
  const toast = useToast();
  const { unlocked: operator } = useOperatorMode();
  const [refreshing, setRefreshing] = useState(false);
  const [recentAudit, setRecentAudit] = useState<AuditEvent[]>([]);
  const clients = state.clients;
  type Mixed = typeof clients[number]['services'][number] & { _client: string; replicas: number; status: string };
  const allServices: Mixed[] = clients.flatMap((c) => c.services.map((s) => ({ ...s, _client: c.name })));

  // Server-side aggregates from /api/v1/dashboard — authoritative source of
  // counts. Local fallback keeps the UI populated if the fetch fails
  // (e.g. backend restart) so the dashboard doesn't go blank.
  const [serverStats, setServerStats] = useState<DashboardSummary | null>(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchDashboard()
        .then((s) => { if (!cancelled) setServerStats(s); })
        .catch((err) => console.error('failed to load dashboard:', err));
    };
    load();
    // Poll so the health/tenant counts stay live, not just on CRUD or reload.
    const t = setInterval(load, 12_000);
    const off = onVisible(load);
    return () => { cancelled = true; clearInterval(t); off(); };
  }, [state.clients.length]); // + immediate refetch after CRUD changes

  // Real audit events for "Recent activity" panel — fetched from BE, refreshed every 15s.
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchAudit({ limit: 7 })
        .then((page) => { if (!cancelled) setRecentAudit(page.items); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 15_000);
    const off = onVisible(load);
    return () => { cancelled = true; clearInterval(t); off(); };
  }, []);

  // Manual refresh — re-fetches both the dashboard summary and the tenants
  // list so the user can force-sync after a known external change (e.g.
  // they just edited resources in another tab).
  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const [summary, clients] = await Promise.all([
        fetchDashboard(),
        fetchTenants(),
      ]);
      setServerStats(summary);
      setState((s) => ({ ...s, clients }));
      toast?.success('Dashboard refreshed', `${summary.tenants.total} tenants · ${summary.services.total} services`);
    } catch (err) {
      toast?.error('Refresh failed', err instanceof ApiError ? err.message : String(err));
    } finally {
      setRefreshing(false);
    }
  };

  // Prefer server counts (which include suspended/provisioning, runtime DB
  // state) over locally-computed ones.
  const healthy = serverStats?.services.healthy ?? allServices.filter((s) => s.status === 'healthy').length;
  const degraded = serverStats?.services.degraded ?? allServices.filter((s) => s.status === 'degraded').length;
  const failing = serverStats?.services.failing ?? allServices.filter((s) => s.status === 'failing').length;
  // Take tenant total + ready from the SAME source (server snapshot when it's
  // complete, else local) — never total-from-server with ready-from-local, which
  // produced wrong / negative "needs attention" counts before both resolved.
  const localReady = clients.filter((c) => c.status === 'healthy' || c.status === 'ready' as any).length;
  const st = serverStats?.tenants;
  const hasServerTenants = st?.total != null && st?.ready != null;
  const tenantCount = hasServerTenants ? st!.total : clients.length;
  const tenantReady = hasServerTenants ? st!.ready! : localReady;
  const tenantAttn = Math.max(0, tenantCount - tenantReady);
  // Average only over services that actually reported metrics (usage >= 0);
  // -1 is the "n/a" sentinel (no k8s metrics) and must not drag the mean down.
  const weightedAvg = (pick: (s: Mixed) => number) => {
    const measured = allServices.filter((s) => pick(s) >= 0);
    const denom = measured.reduce((a, s) => a + s.replicas, 0);
    if (denom === 0) return -1; // nothing measured → n/a
    return Math.round(measured.reduce((a, s) => a + pick(s) * s.replicas, 0) / denom);
  };
  const avgCpu = weightedAvg((s) => s.cpuUsage);
  const avgMem = weightedAvg((s) => s.memUsage);

  // Real nodes from BE — list, status, pressure metrics. Falls back to
  // empty list when k8s backend is noop'd (`K8S_BACKEND=noop`); we render
  // a friendly empty state instead of fake data.
  const [nodes, setNodes] = useState<NodeStatus[]>([]);
  const [selectedNode, setSelectedNode] = useState<NodeStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchNodes()
      .then((n) => {
        if (cancelled) return;
        setNodes(n); // render the overview immediately…
        // …then lazily fill in the expensive GPU/disk cells.
        fetchNodeMetrics()
          .then((m) => { if (!cancelled) setNodes((prev) => mergeNodeMetrics(prev, m)); })
          .catch((err) => console.error('failed to load node metrics:', err));
      })
      .catch((err) => console.error('failed to load nodes:', err));
    return () => { cancelled = true; };
  }, [state.clients.length, refreshing]); // refresh on dashboard refresh too

  // Re-pull nodes (+ metrics) and refresh the open detail modal — called after
  // a mutation like setting GPU slots so the UI reflects the new value.
  const refreshNodes = () => {
    fetchNodes()
      .then((n) => {
        setNodes(n);
        setSelectedNode((cur) => (cur ? n.find((x) => x.name === cur.name) ?? cur : cur));
        fetchNodeMetrics()
          .then((m) => {
            const merged = mergeNodeMetrics(n, m);
            setNodes(merged);
            setSelectedNode((cur) => (cur ? merged.find((x) => x.name === cur.name) ?? cur : cur));
          })
          .catch(() => {});
      })
      .catch(() => {});
  };

  // Live API traffic comes from the BE's own /metrics (RED) via the
  // ApiTrafficPanel below — a real, honest time-series built from polling,
  // not the old hardcoded fake trend.

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Cluster overview</h1>
          <p className="page-sub">Real-time status across all client and global workloads.</p>
        </div>
        <div className="row">
          <span className={'badge ' + (failing > 0 ? 'badge-red' : degraded > 0 ? 'badge-amber' : 'badge-green')}>
            <span className="dot"></span>
            {failing > 0 ? `${failing} failing` : degraded > 0 ? `${degraded} degraded` : 'cluster healthy'}
          </span>
          <button className="btn btn-sm" onClick={doRefresh} disabled={refreshing}>
            <I.Refresh size={13}/> {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="metric-grid">
        <div className="metric">
          <div className="metric-label"><I.Users size={11}/> Clients</div>
          <div className="metric-value">{tenantCount}</div>
          <div className="metric-sub">{tenantReady} ready · {tenantAttn} need attention</div>
        </div>
        <div className="metric">
          <div className="metric-label"><I.Cube size={11}/> Services running</div>
          <div className="metric-value">{healthy}<span style={{ fontSize: 14, color: 'var(--text-3)', fontWeight: 500 }}> / {serverStats?.services.total ?? allServices.length}</span></div>
          <div className="metric-sub" style={{ color: degraded ? 'var(--amber)' : 'var(--text-2)' }}>
            {failing > 0 && <span style={{ color: 'var(--red)' }}>{failing} failing · </span>}
            {degraded} degraded
          </div>
        </div>
        <div className="metric">
          <div className="metric-label"><I.Cpu size={11}/> Avg CPU</div>
          <div className="metric-value">{avgCpu < 0 ? <span style={{ color: 'var(--text-3)' }}>n/a</span> : <>{avgCpu}<span style={{ fontSize: 14, color: 'var(--text-3)' }}>%</span></>}</div>
          <div className="metric-spark"><ResourceBar value={avgCpu}/></div>
        </div>
        <div className="metric">
          <div className="metric-label"><I.Memory size={11}/> Avg memory</div>
          <div className="metric-value">{avgMem < 0 ? <span style={{ color: 'var(--text-3)' }}>n/a</span> : <>{avgMem}<span style={{ fontSize: 14, color: 'var(--text-3)' }}>%</span></>}</div>
          <div className="metric-spark"><ResourceBar value={avgMem}/></div>
        </div>
        {/* Cluster GPU utilization — average real compute util across GPU nodes
            (DCGM). n/a when no GPU nodes / no DCGM source. Operator-gated: GPU
            status (dashes/util) is hidden from the client view. */}
        {operator && (() => {
          const gpuNodes = nodes.filter((n) => n.gpuCapacity > 0 && n.gpuUtilPercent >= 0);
          const avgGpu = gpuNodes.length ? Math.round(gpuNodes.reduce((a, n) => a + n.gpuUtilPercent, 0) / gpuNodes.length) : -1;
          return (
            <div className="metric">
              <div className="metric-label"><I.Cpu size={11}/> Avg GPU</div>
              <div className="metric-value">{avgGpu < 0 ? <span style={{ color: 'var(--text-3)' }}>n/a</span> : <>{avgGpu}<span style={{ fontSize: 14, color: 'var(--text-3)' }}>%</span></>}</div>
              <div className="metric-spark"><ResourceBar value={avgGpu}/></div>
            </div>
          );
        })()}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
        <div className="card">
          <div className="card-head">
            <div>
              <h3>Nodes ({nodes.length})</h3>
              <p>Real cluster nodes · click a row for detail · pressure from <span className="mono">kubectl top</span></p>
            </div>
          </div>
          <div style={{ padding: 8 }}>
            {nodes.length === 0 && (
              <div className="muted" style={{ padding: 16, textAlign: 'center', fontSize: 12.5 }}>
                No node data — the live cluster backend isn’t enabled on this instance.
              </div>
            )}
            {nodes.map((n) => (
              <div key={n.name} className="row-link" onClick={() => setSelectedNode(n)}
                   style={{ display: 'grid', gridTemplateColumns: `16px 1fr 70px 70px${operator ? ' 70px' : ''}${operator && n.gpuCapacity > 0 ? ' 70px' : ''}`,
                            alignItems: 'center', gap: 10, padding: '9px 12px',
                            borderRadius: 6, cursor: 'pointer' }}>
                <StatusDot status={n.ready && !n.cordoned ? 'healthy' : n.cordoned ? 'degraded' : 'failing'}/>
                <div>
                  <div className="mono" style={{ fontSize: 12.5 }}>
                    {n.name}
                    {n.taints && n.taints.length > 0 && (
                      <span className="badge badge-amber" style={{ marginLeft: 6, fontSize: 10 }}>tainted</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    {n.podCount}/{n.podCapacity || '?'} pods · {n.roles.join(', ')}
                  </div>
                </div>
                <div>{n.cpuPercent >= 0 ? <ResourceBar value={n.cpuPercent} label="cpu"/> : <span className="muted" style={{ fontSize: 11 }}>—</span>}</div>
                <div>{n.memPercent >= 0 ? <ResourceBar value={n.memPercent} label="mem"/> : <span className="muted" style={{ fontSize: 11 }}>—</span>}</div>
                {operator && (
                  <div>{n.diskPercent >= 0 ? <ResourceBar value={n.diskPercent} label="disk"/> : <span className="muted" style={{ fontSize: 11 }}>—</span>}</div>
                )}
                {operator && n.gpuCapacity > 0 && (
                  <div>{n.gpuUtilPercent >= 0 ? <ResourceBar value={n.gpuUtilPercent} label="gpu"/> : <span className="muted" style={{ fontSize: 11 }}>—</span>}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <h3>Recent activity</h3>
              <p>Last events from admin & ArgoCD</p>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={() => onNav({ page: 'audit' })}>Open audit log <I.ChevronR size={12}/></button>
          </div>
          <div style={{ padding: '6px 18px 14px' }}>
            {recentAudit.length === 0 && (
              <div className="muted" style={{ fontSize: 12.5, padding: '8px 0' }}>No recent activity.</div>
            )}
            {recentAudit.map((e) => (
              <div className="feed-row" key={e.id}>
                <div className="feed-time">{timeAgo(e.createdAt)}</div>
                <div>
                  <div>
                    <span className="mono" style={{ color: 'var(--text-1)' }}>{e.actorEmail}</span>
                    <span className="muted"> {e.action} </span>
                    <span className="mono" style={{ color: 'var(--accent)' }}>{e.target}</span>
                  </div>
                  {e.payload && typeof e.payload === 'object' && Object.keys(e.payload as object).length > 0 && (
                    <div className="muted mono" style={{ fontSize: 11.5, marginTop: 2 }}>
                      {Object.entries(e.payload as Record<string, unknown>).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedNode && <NodeDetailModal node={selectedNode} onClose={() => setSelectedNode(null)} onChanged={refreshNodes}/>}

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-head">
          <div>
            <h3>Needs attention</h3>
            <p>Workloads not in a healthy state</p>
          </div>
        </div>
        <div style={{ padding: '4px 0' }}>
          {(() => {
            // Only genuinely-bad states — not transient ones (provisioning /
            // pending / restarting / starting / syncing) which are normal and
            // would otherwise flag every fresh client as a "problem".
            const problem = new Set(['failing', 'degraded', 'unknown']);
            const items = clients.flatMap((c) =>
              c.services.filter((s) => problem.has(s.status)).map((s) => ({ ...s, _client: c.id })),
            );
            if (items.length === 0) {
              return <Empty icon={<I.CheckCirc size={28}/>} title="Everything looks good" message="No degraded or failing workloads right now."/>;
            }
            return items.map((it, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '20px 1fr auto auto',
                                       alignItems: 'center', gap: 14, padding: '11px 22px',
                                       borderBottom: '1px solid var(--border)' }}>
                <StatusDot status={it.status}/>
                <div>
                  <div className="row" style={{ gap: 8 }}>
                    <span className="mono" style={{ fontSize: 13 }}>{it._client}/{it.name}</span>
                    <StatusBadge status={it.status}/>
                  </div>
                  <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                    {it.readyReplicas} of {it.replicas} replicas ready · node <span className="mono">{it.nodeName}</span>
                  </div>
                </div>
                <button className="btn btn-sm" onClick={() => onNav({ page: 'client-detail', id: it._client, tab: 'services' })}>
                  Open
                </button>
              </div>
            ));
          })()}
        </div>
      </div>

      <ApiTrafficPanel/>
    </div>
  );
}

// ApiTrafficPanel polls the backend's own /metrics (RED) summary and renders
// a live view: requests/sec (derived from the delta between polls), average
// latency, server-error rate, in-flight requests, a req/s sparkline, and the
// busiest routes. No external Prometheus needed — the numbers live in the BE.
function ApiTrafficPanel() {
  const POLL_MS = 2500;
  const [summary, setSummary] = useState<MetricsSummary | null>(null);
  const [rate, setRate] = useState(0);
  const [history, setHistory] = useState<number[]>([]);
  const [offline, setOffline] = useState(false);
  const prev = useRef<{ total: number; t: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await fetchMetricsSummary();
        if (cancelled) return;
        setSummary(s);
        setOffline(false);
        const now = Date.now();
        if (prev.current) {
          const dt = (now - prev.current.t) / 1000;
          const r = dt > 0 ? Math.max(0, (s.totalRequests - prev.current.total) / dt) : 0;
          setRate(r);
          setHistory((h) => [...h.slice(-29), r]);
        }
        prev.current = { total: s.totalRequests, t: now };
      } catch {
        if (!cancelled) setOffline(true);
      }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    const off = onVisible(tick);
    return () => { cancelled = true; clearInterval(id); off(); };
  }, []);

  const total = summary?.totalRequests ?? 0;
  const errors5xx = summary?.byStatusClass?.['5xx'] ?? 0;
  const errRate = total > 0 ? (errors5xx / total) * 100 : 0;

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-head">
        <div>
          <h3>API traffic (live)</h3>
          <p>From the backend's own <span className="mono">/metrics</span> · polled every {POLL_MS / 1000}s</p>
        </div>
        <span className={'badge ' + (offline ? '' : 'badge-green')}>
          <span className="dot"></span>{offline ? 'metrics unavailable' : `${total.toLocaleString()} requests total`}
        </span>
      </div>

      <div style={{ padding: 14 }}>
        <div className="metric-grid">
          <div className="metric">
            <div className="metric-label"><I.Refresh size={11}/> Requests / sec</div>
            <div className="metric-value">{rate.toFixed(rate < 10 ? 1 : 0)}</div>
            <div className="metric-sub">{summary ? `${total.toLocaleString()} since start` : 'connecting…'}</div>
          </div>
          <div className="metric">
            <div className="metric-label"><I.Cpu size={11}/> Avg latency</div>
            <div className="metric-value">{(summary?.avgLatencyMs ?? 0).toFixed(1)}<span style={{ fontSize: 14, color: 'var(--text-3)' }}> ms</span></div>
            <div className="metric-sub">across all routes</div>
          </div>
          <div className="metric">
            <div className="metric-label"><I.XCirc size={11}/> Server errors</div>
            <div className="metric-value" style={{ color: errRate > 0 ? 'var(--red)' : undefined }}>
              {errRate.toFixed(1)}<span style={{ fontSize: 14, color: 'var(--text-3)' }}>%</span>
            </div>
            <div className="metric-sub">{errors5xx} of {total.toLocaleString()} (5xx)</div>
          </div>
          <div className="metric">
            <div className="metric-label"><I.Refresh size={11}/> In flight</div>
            <div className="metric-value">{summary?.inFlight ?? 0}</div>
            <div className="metric-sub">requests being served now</div>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <div className="spread" style={{ fontSize: 11.5, marginBottom: 4 }}>
            <span className="muted">requests / sec</span>
            <span className="muted">last ~{Math.round((history.length * POLL_MS) / 1000)}s</span>
          </div>
          <Sparkline data={history.length ? history : [0, 0]} height={44}/>
        </div>

        {summary && summary.routes.length > 0 && (
          <table className="tbl" style={{ marginTop: 14 }}>
            <thead><tr><th>Method</th><th>Route</th><th style={{ textAlign: 'right' }}>Requests</th><th style={{ textAlign: 'right' }}>Avg</th></tr></thead>
            <tbody>
              {summary.routes.map((r) => (
                <tr key={r.method + r.route}>
                  <td><span className="badge" style={{ fontSize: 10.5 }}>{r.method}</span></td>
                  <td className="mono" style={{ fontSize: 12 }}>{r.route}</td>
                  <td className="mono num" style={{ textAlign: 'right' }}>{r.count.toLocaleString()}</td>
                  <td className="mono num" style={{ textAlign: 'right' }}>{r.avgMs.toFixed(1)} ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// NodeDetailModal shows everything we know about a node: metadata the summary
// row hides (taints, kubelet, OS, arch, cordoned) plus the pods actually
// scheduled on it, grouped by tenant. The taints + pod placement together let
// an operator confirm where a per-service-pinned workload actually landed.
function NodeDetailModal({ node, onClose, onChanged }: { node: NodeStatus; onClose: () => void; onChanged?: () => void }) {
  const [pods, setPods] = useState<PodInfo[] | null>(null);
  const toast = useToast();
  const { unlocked: operator } = useOperatorMode();
  // Seed from the *configured* slots (the node label = what was last set), not
  // allocatable GpuCapacity, which lags a device-plugin reload behind.
  const [slots, setSlots] = useState(Math.max(1, node.gpuConfiguredSlots || node.gpuCapacity || 1));
  const [savingSlots, setSavingSlots] = useState(false);
  const applySlots = async () => {
    setSavingSlots(true);
    try {
      await setNodeGPUSlots(node.name, slots);
      toast?.success('GPU slots updated', `${node.name}: up to ${slots} pod${slots === 1 ? '' : 's'} per GPU (applies after the device plugin reloads)`);
      onChanged?.(); // refresh the node list so a reopen shows the new value
    } catch (e) {
      toast?.error('Failed to set GPU slots', e instanceof ApiError ? e.message : String(e));
    } finally {
      setSavingSlots(false);
    }
  };
  useEffect(() => {
    let cancelled = false;
    fetchNodePods(node.name)
      .then((p) => { if (!cancelled) setPods(p); })
      .catch(() => { if (!cancelled) setPods([]); });
    return () => { cancelled = true; };
  }, [node.name]);

  // Group pods by tenant ("" = system / infra).
  const groups = (pods ?? []).reduce<Record<string, PodInfo[]>>((acc, p) => {
    const k = p.tenant || '__system__';
    (acc[k] ||= []).push(p);
    return acc;
  }, {});
  const groupKeys = Object.keys(groups).sort((a, b) => (a === '__system__' ? 1 : b === '__system__' ? -1 : a.localeCompare(b)));

  return (
    <Modal open onClose={onClose} size="lg"
           title={<span className="mono">{node.name}</span>}
           subtitle={`${node.roles.join(', ') || 'node'} · kubelet ${node.kubeletVersion} · ${node.osImage}`}>
      <div className="col" style={{ gap: 14 }}>
        {/* Metadata */}
        <div className="res-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12.5 }}>
          <KVrow label="Status" value={
            <span className={'badge ' + (node.ready && !node.cordoned ? 'badge-green' : 'badge-amber')}>
              {node.cordoned ? 'cordoned' : node.ready ? 'ready' : 'not ready'}
            </span>
          }/>
          <KVrow label="Pods" value={`${node.podCount} / ${node.podCapacity || '?'}`}/>
          <KVrow label="Architecture" value={<span className="mono">{node.architecture}</span>}/>
          <KVrow label="CPU / Mem" value={
            <span className="mono">
              {node.cpuPercent >= 0 ? `${node.cpuPercent}%` : 'n/a'} / {node.memPercent >= 0 ? `${node.memPercent}%` : 'n/a'}
            </span>
          }/>
          {operator && node.diskPercent >= 0 && (
            <div style={{ gridColumn: 'span 2', padding: '4px 0 2px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, fontSize: 12 }}>
                <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>Disk</span>
                <span className="mono" style={{ fontSize: 12 }}>{node.diskPercent}% · {fmtBytes(node.diskBytes)} / {fmtBytes(node.diskCapacityBytes)}</span>
              </div>
              <ResourceBar value={node.diskPercent}/>
            </div>
          )}
          {node.gpuCapacity > 0 ? (
            <div style={{ gridColumn: 'span 2', padding: '4px 0 2px' }}>
              {/* Read-only GPU status — operator-gated (dashes/util hidden from
                  the client view). The slots setter below stays available. */}
              {operator && (
                <>
                  <div className="spread" style={{ marginBottom: 6, fontSize: 12 }}>
                    <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>GPU</span>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {node.gpuCapacity} GPU{node.gpuCapacity !== 1 ? 's' : ''}
                      {node.gpuPercent >= 0 && <span className="muted"> · {node.gpuPercent}% allocated</span>}
                    </span>
                  </div>
                  {/* Compute utilization (DCGM) */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--text-3)', marginBottom: 3 }}>
                    <span>Compute util</span>
                    <span className="mono">{node.gpuUtilPercent >= 0 ? `${node.gpuUtilPercent}%` : 'n/a'}</span>
                  </div>
                  <ResourceBar value={node.gpuUtilPercent}/>
                  {/* VRAM (GPU memory) */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--text-3)', margin: '8px 0 3px' }}>
                    <span>VRAM</span>
                    <span className="mono">{node.gpuMemCapacityBytes > 0 ? `${fmtBytes(node.gpuMemUsedBytes)} / ${fmtBytes(node.gpuMemCapacityBytes)}` : 'n/a'}</span>
                  </div>
                  <ResourceBar value={node.gpuMemCapacityBytes > 0 ? Math.round(node.gpuMemUsedBytes * 100 / node.gpuMemCapacityBytes) : -1}/>
                </>
              )}
              {/* Time-slicing: how many pods may share one physical GPU. This is
                  an operator action, kept available regardless of operator mode. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <span style={{ fontSize: 11.5, color: 'var(--text-3)', flex: 1 }}>Pods per GPU (time-slicing)</span>
                <input className="input" type="number" min={1} max={64} value={slots}
                       onChange={(e) => setSlots(Math.max(1, Math.min(64, Number(e.target.value) || 1)))}
                       style={{ width: 72, height: 28, padding: '0 8px' }}/>
                <button className="btn btn-primary btn-sm" disabled={savingSlots} onClick={applySlots}>
                  {savingSlots ? 'Applying…' : 'Apply'}
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                1 = exclusive (one pod per GPU). Higher = more pods share it (memory is shared, not partitioned).
                {node.gpuConfiguredSlots > 0 && (
                  <> · configured: <span className="mono">{node.gpuConfiguredSlots}</span>
                    {node.gpuCapacity > 0 && node.gpuCapacity !== node.gpuConfiguredSlots &&
                      <> · live: <span className="mono">{node.gpuCapacity}</span> (device plugin reloading…)</>}
                  </>
                )}
              </div>
            </div>
          ) : operator ? (
            <KVrow label="GPU" value={<span className="mono muted">n/a (no GPU on node)</span>}/>
          ) : null}
        </div>

        {/* Taints — node scheduling constraints */}
        <div>
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Taints</h4>
          {node.taints && node.taints.length > 0 ? (
            <div className="col" style={{ gap: 4 }}>
              {node.taints.map((t) => (
                <span key={t} className="badge badge-amber mono" style={{ fontSize: 11.5, alignSelf: 'flex-start' }}>{t}</span>
              ))}
            </div>
          ) : (
            <span className="muted" style={{ fontSize: 12 }}>None — shared scheduling.</span>
          )}
        </div>

        {/* Pods on this node, grouped by tenant */}
        <div>
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            Pods on this node{pods ? ` (${pods.length})` : ''}
          </h4>
          {pods === null && <span className="muted" style={{ fontSize: 12 }}>Loading…</span>}
          {pods !== null && pods.length === 0 && <span className="muted" style={{ fontSize: 12 }}>No pods scheduled here.</span>}
          {groupKeys.map((g) => (
            <div key={g} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 500, margin: '8px 0 5px', color: 'var(--text-2)' }}>
                {g === '__system__'
                  ? <span className="muted">system / infra</span>
                  : <>tenant <span className="mono" style={{ color: 'var(--accent)', fontSize: 13.5 }}>{g}</span></>}
              </div>
              <table className="tbl">
                <thead><tr><th>Pod</th><th>Status</th><th>Ready</th><th>Restarts</th><th>Age</th></tr></thead>
                <tbody>
                  {groups[g].map((p) => (
                    <tr key={p.namespace + '/' + p.name}>
                      <td className="mono" style={{ fontSize: 11.5 }}>{p.name}</td>
                      <td><StatusBadge status={p.phase === 'Running' ? 'healthy' : p.phase === 'Pending' ? 'pending' : 'failing'}/></td>
                      <td className="mono num">{p.ready}</td>
                      <td className="mono num" style={{ color: p.restarts > 0 ? 'var(--amber)' : undefined }}>{p.restarts}</td>
                      <td className="mono muted" style={{ fontSize: 11.5 }}>{p.age}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// NodesScreen is the dedicated /nodes page reached from the sidebar — the full
// cluster node list (the dashboard only shows a compact panel). Rows open the
// same NodeDetailModal (metadata + taints + pods).
export function NodesScreen() {
  const { unlocked: operator } = useOperatorMode();
  const [nodes, setNodes] = useState<NodeStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<NodeStatus | null>(null);
  const nodesRef = useRef<NodeStatus[]>([]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchNodes()
        .then((n) => {
          if (cancelled) return;
          // Carry forward the expensive GPU/disk cells so a poll doesn't blank
          // them until the slower metrics scrape resolves (no first-load cells).
          setNodes((prev) => {
            const byName = new Map(prev.map((x) => [x.name, x]));
            return n.map((node) => {
              const old = byName.get(node.name);
              return old ? {
                ...node,
                gpuUtilPercent: old.gpuUtilPercent, gpuMemUsedBytes: old.gpuMemUsedBytes,
                gpuMemCapacityBytes: old.gpuMemCapacityBytes,
                gpuCapacity: node.gpuCapacity > 0 ? node.gpuCapacity : old.gpuCapacity,
                diskPercent: old.diskPercent, diskBytes: old.diskBytes, diskCapacityBytes: old.diskCapacityBytes,
              } : node;
            });
          });
          setLoading(false);
          // …then lazily fill in the expensive GPU/disk cells.
          fetchNodeMetrics()
            .then((m) => { if (!cancelled) setNodes((prev) => mergeNodeMetrics(prev, m)); })
            .catch(() => { /* metrics are best-effort; the list already renders */ });
        })
        .catch(() => { if (!cancelled) setLoading(false); });
    };
    load();
    // Poll so the list stays fresh AND a failed initial fetch (e.g. a transient
    // 429 during a page-load burst) recovers on the next tick instead of leaving
    // the page stuck on "No node data" until a manual reload.
    const id = setInterval(load, 12000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Re-pull nodes (+ metrics) and refresh the open detail modal — called after
  // a mutation like setting GPU slots so the UI reflects the new value.
  const refreshNodes = () => {
    fetchNodes()
      .then((n) => {
        // Update the list + open detail IMMEDIATELY (so a just-changed value like
        // GPU slots shows at once), carrying forward the last-known disk/GPU cells
        // so they don't blank out while the slower metrics scrape runs.
        const prev = nodesRef.current;
        const byName = new Map(prev.map((p) => [p.name, p]));
        const carried = n.map((node) => {
          const old = byName.get(node.name);
          return old ? {
            ...node,
            gpuUtilPercent: old.gpuUtilPercent, gpuMemUsedBytes: old.gpuMemUsedBytes,
            gpuMemCapacityBytes: old.gpuMemCapacityBytes,
            gpuCapacity: node.gpuCapacity > 0 ? node.gpuCapacity : old.gpuCapacity,
            diskPercent: old.diskPercent, diskBytes: old.diskBytes, diskCapacityBytes: old.diskCapacityBytes,
          } : node;
        });
        setNodes(carried);
        setSelectedNode((cur) => (cur ? carried.find((x) => x.name === cur.name) ?? cur : cur));
        // …then overlay fresh metrics when the scrape resolves.
        fetchNodeMetrics()
          .then((m) => {
            const merged = mergeNodeMetrics(carried, m);
            setNodes(merged);
            setSelectedNode((cur) => (cur ? merged.find((x) => x.name === cur.name) ?? cur : cur));
          })
          .catch(() => {});
      })
      .catch(() => {});
  };

  // Auto-refresh on a timer so node CPU/mem/GPU/disk stay live like the rest of
  // the app (the initial load + manual refresh don't cover idle viewing). 20s —
  // node metrics are relatively expensive (kubectl + DCGM scrapes per node).
  // Also refresh the moment the tab regains focus, so returning to a
  // long-open tab shows fresh data without a manual page reload.
  useEffect(() => {
    const id = setInterval(refreshNodes, 20_000);
    const off = onVisible(refreshNodes);
    return () => { clearInterval(id); off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Cluster nodes</h1>
          <p className="page-sub">Real nodes from <span className="mono">kubectl</span> · click a row for pods, taints &amp; metadata</p>
        </div>
        <span className="badge badge-green"><span className="dot"></span>{nodes.length} node{nodes.length === 1 ? '' : 's'}</span>
      </div>

      <div className="card">
        {loading && (
          <div className="col" style={{ alignItems: 'center', gap: 12, padding: '44px 16px', color: 'var(--text-3)', fontSize: 13 }}>
            <div className="spinner"/>
            Loading cluster nodes…
          </div>
        )}
        {!loading && nodes.length === 0 && (
          <Empty icon={<I.Server size={28}/>} title="No node data"
                 message="The live cluster backend isn’t enabled on this instance, so there are no nodes to show."/>
        )}
        {nodes.length > 0 && (
          <table className="tbl">
            <thead>
              <tr><th></th><th>Node</th><th>Status</th><th>Pods</th><th>CPU</th><th>Mem</th>{operator && <th>Disk</th>}{operator && <th>GPU</th>}<th>Kubelet</th><th></th></tr>
            </thead>
            <tbody>
              {nodes.map((n) => (
                <tr key={n.name} className="row-link" onClick={() => setSelectedNode(n)}>
                  <td><StatusDot status={n.ready && !n.cordoned ? 'healthy' : n.cordoned ? 'degraded' : 'failing'}/></td>
                  <td>
                    <span className="mono" style={{ fontSize: 12.5 }}>{n.name}</span>
                    {n.taints && n.taints.length > 0 && (
                      <span className="badge badge-amber" style={{ marginLeft: 6, fontSize: 10 }}>tainted</span>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{n.osImage} · {n.architecture}</div>
                  </td>
                  <td>
                    <span className={'badge ' + (n.ready && !n.cordoned ? 'badge-green' : 'badge-amber')}>
                      {n.cordoned ? 'cordoned' : n.ready ? 'ready' : 'not ready'}
                    </span>
                  </td>
                  <td className="mono num">{n.podCount}/{n.podCapacity || '?'}</td>
                  <td style={{ width: 80 }}>{n.cpuPercent >= 0 ? <ResourceBar value={n.cpuPercent}/> : <span className="muted" style={{ fontSize: 11 }}>—</span>}</td>
                  <td style={{ width: 80 }}>{n.memPercent >= 0 ? <ResourceBar value={n.memPercent}/> : <span className="muted" style={{ fontSize: 11 }}>—</span>}</td>
                  {operator && <td style={{ width: 80 }}>{n.diskPercent >= 0 ? <ResourceBar value={n.diskPercent}/> : <span className="muted" style={{ fontSize: 11 }}>—</span>}</td>}
                  {operator && <td style={{ width: 80 }}>{n.gpuCapacity > 0 ? (n.gpuUtilPercent >= 0 ? <ResourceBar value={n.gpuUtilPercent} label="gpu"/> : <span className="muted" style={{ fontSize: 11 }}>—</span>) : <span className="muted" style={{ fontSize: 11 }}>—</span>}</td>}
                  <td className="mono muted" style={{ fontSize: 11.5 }}>{n.kubeletVersion}</td>
                  <td><I.ChevronR size={14} style={{ color: 'var(--text-3)' }}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedNode && <NodeDetailModal node={selectedNode} onClose={() => setSelectedNode(null)} onChanged={refreshNodes}/>}
    </div>
  );
}

// ─── EVENTS (cluster-wide k8s events panel) ─────────────────────────────────
export function EventsScreen() {
  const [events, setEvents] = useState<ClusterEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [warningsOnly, setWarningsOnly] = useState(false);
  const [tenantFilter, setTenantFilter] = useState('');
  const [q, setQ] = useState('');

  const load = () => {
    fetchEvents({ warningsOnly, tenant: tenantFilter || undefined, limit: 300 })
      .then((d) => { setEvents(d); setErr(null); setLoading(false); })
      .catch((e) => { setErr(e instanceof Error ? e.message : String(e)); setLoading(false); });
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    const off = onVisible(load);
    return () => { clearInterval(id); off(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warningsOnly, tenantFilter]);

  // Client-side text filter on top of server-side type+namespace narrowing.
  // Lower-case search across object/reason/message/namespace — operator can
  // type a pod name and the table narrows in real time.
  const ql = q.trim().toLowerCase();
  const shown = ql === ''
    ? events
    : events.filter((e) =>
        e.object.toLowerCase().includes(ql) ||
        e.reason.toLowerCase().includes(ql) ||
        e.message.toLowerCase().includes(ql) ||
        e.namespace.toLowerCase().includes(ql));

  const warnings = events.filter((e) => e.type === 'Warning').length;

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Cluster events</h1>
          <p className="page-sub">Live <span className="mono">kubectl get events -A</span> · auto-refresh 5s · newest first</p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <span className="badge badge-amber" title="Warning-class events in the current view">
            <span className="dot"></span>{warnings} warning{warnings === 1 ? '' : 's'}
          </span>
          <span className="badge badge-green"><span className="dot"></span>{events.length} total</span>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-pad row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 240px' }}>
            <I.Search size={13} style={{ position: 'absolute', left: 9, top: 7, color: 'var(--text-3)' }}/>
            <input className="input" style={{ paddingLeft: 28 }}
                   placeholder="Filter object/reason/message…" value={q} onChange={(e) => setQ(e.target.value)}/>
          </div>
          <input className="input input-mono" style={{ width: 220 }}
                 placeholder="Tenant ID (empty = all)"
                 value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value.trim())}/>
          <label className="row" style={{ gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={warningsOnly} onChange={(e) => setWarningsOnly(e.target.checked)}/>
            Warnings only
          </label>
          <span className="muted" style={{ marginLeft: 'auto', fontSize: 12 }}>{shown.length} shown</span>
        </div>
      </div>

      <div className="card">
        {loading && <div className="muted" style={{ padding: 16, fontSize: 13 }}>Loading…</div>}
        {err && <div className="field-error" style={{ padding: 12 }}>Failed to load events: {err}</div>}
        {!loading && !err && shown.length === 0 && (
          <Empty icon={<I.Activity size={28}/>} title="No events"
                 message="Either the cluster is happy or kubectl can't reach it (K8S_BACKEND=noop returns empty)."/>
        )}
        {shown.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 90 }}>When</th>
                <th style={{ width: 80 }}>Type</th>
                <th style={{ width: 140 }}>Reason</th>
                <th>Object</th>
                <th style={{ width: 130 }}>Namespace</th>
                <th>Message</th>
                <th style={{ width: 50, textAlign: 'right' }}>×</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((e, i) => (
                <tr key={`${e.lastSeen}-${e.object}-${i}`}>
                  <td className="muted" style={{ fontSize: 12 }}>{relativeTime(e.lastSeen)}</td>
                  <td>
                    <span className={'badge ' + (e.type === 'Warning' ? 'badge-amber' : 'badge-green')}>
                      {e.type || '—'}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>{e.reason}</td>
                  <td className="mono" style={{ fontSize: 12 }}>{e.object}</td>
                  <td>
                    <span className="mono" style={{ fontSize: 11.5 }}>{e.namespace}</span>
                    {e.tenant && <div className="muted" style={{ fontSize: 10.5 }}>tenant: {e.tenant}</div>}
                  </td>
                  <td style={{ fontSize: 12.5, maxWidth: 480 }}>{e.message}</td>
                  <td className="mono num">{e.count > 1 ? e.count : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── TRASH (soft-deleted tenants, restorable within 30 days) ────────────────
export function TrashScreen() {
  const [items, setItems] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<Record<string, boolean>>({});
  const [purging, setPurging] = useState<Record<string, boolean>>({});
  const toast = useToast();

  const load = () => {
    fetchDeletedTenants()
      .then((d) => { setItems(d); setErr(null); setLoading(false); })
      .catch((e) => { setErr(e instanceof Error ? e.message : String(e)); setLoading(false); });
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    const off = onVisible(load);
    return () => { clearInterval(id); off(); };
  }, []);

  // confirmTarget holds the row the user clicked Restore on; null = no modal.
  // Using a proper Modal instead of window.confirm gives us a dashboard-
  // styled dialog with full context (display name, deleted-when, purge
  // window, service list) before the BE is hit.
  const [confirmTarget, setConfirmTarget] = useState<Client | null>(null);
  // purgeTarget drives the separate, type-to-confirm "delete permanently" modal.
  const [purgeTarget, setPurgeTarget] = useState<Client | null>(null);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  // "Delete all" — type-to-confirm bulk purge of every soft-deleted tenant.
  const [purgeAllOpen, setPurgeAllOpen] = useState(false);
  const [purgeAllConfirm, setPurgeAllConfirm] = useState('');
  const [purgingAll, setPurgingAll] = useState(false);

  const doRestore = async () => {
    const c = confirmTarget;
    if (!c) return;
    setConfirmTarget(null);
    setRestoring((m) => ({ ...m, [c.id]: true }));
    try {
      await recoverTenant(c.id);
      toast?.success(`Restored ${c.displayName || c.id}`);
      setItems((cur) => cur.filter((x) => x.id !== c.id));
    } catch (e) {
      toast?.error(`Restore failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRestoring((m) => { const { [c.id]: _drop, ...rest } = m; return rest; });
    }
  };

  const doPurge = async () => {
    const c = purgeTarget;
    if (!c) return;
    setPurgeTarget(null);
    setPurgeConfirm('');
    setPurging((m) => ({ ...m, [c.id]: true }));
    try {
      await purgeTenant(c.id);
      toast?.success(`Permanently deleted ${c.displayName || c.id}`);
      setItems((cur) => cur.filter((x) => x.id !== c.id));
    } catch (e) {
      toast?.error(`Permanent delete failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setPurging((m) => { const { [c.id]: _drop, ...rest } = m; return rest; });
    }
  };

  // Purge every soft-deleted tenant in a single server-side request — avoids the
  // per-request rate limiter that a client-side loop trips (429s halfway).
  const doPurgeAll = async () => {
    setPurgeAllOpen(false);
    setPurgeAllConfirm('');
    setPurgingAll(true);
    try {
      const { purged, failed } = await purgeAllDeleted();
      setItems([]);
      if (failed === 0) toast?.success(`Permanently deleted ${purged} tenant${purged === 1 ? '' : 's'}`);
      else toast?.error(`Deleted ${purged}, ${failed} failed`);
    } catch (e) {
      toast?.error(`Delete all failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setPurgingAll(false);
      load();
    }
  };

  // daysLeft = ceil((purgeAt - now) / 1 day). Used to color "expiring soon" rows.
  const daysLeft = (purgeAt?: string): number | null => {
    if (!purgeAt) return null;
    const ms = new Date(purgeAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86_400_000));
  };

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Trash</h1>
          <p className="page-sub">Soft-deleted tenants. Restorable within 30 days; after that the pruner hard-deletes them.</p>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <span className="badge badge-amber"><span className="dot"></span>{items.length} deleted</span>
          {items.length > 0 && (
            <button className="btn btn-sm btn-danger" disabled={purgingAll} onClick={() => setPurgeAllOpen(true)}>
              <I.Trash size={13}/> {purgingAll ? 'Deleting…' : 'Delete all'}
            </button>
          )}
        </div>
      </div>

      <div className="card">
        {loading && <div className="muted" style={{ padding: 16, fontSize: 13 }}>Loading…</div>}
        {err && <div className="field-error" style={{ padding: 12 }}>Failed to load trash: {err}</div>}
        {!loading && !err && items.length === 0 && (
          <Empty icon={<I.Trash size={28}/>} title="Trash is empty"
                 message="No soft-deleted tenants. Deleting a tenant moves it here for 30 days."/>
        )}
        {items.length > 0 && (
          <table className="tbl">
            <thead>
              <tr><th>Client</th><th>Plan</th><th>Deleted</th><th>Purged in</th><th>Services</th><th style={{ textAlign: 'right' }}></th></tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const left = daysLeft(c.purgeAt);
                const urgent = left != null && left <= 7;
                return (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{c.displayName || c.id}</div>
                      <div className="mono muted" style={{ fontSize: 11 }}>{c.id}</div>
                    </td>
                    <td><span className="badge">{c.plan}</span></td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {c.deletedAt ? relativeTime(c.deletedAt) : '—'}
                    </td>
                    <td>
                      <span className={'badge ' + (urgent ? 'badge-red' : 'badge-amber')}>
                        {left == null ? '—' : left === 0 ? 'any moment' : `${left}d`}
                      </span>
                    </td>
                    <td className="mono muted" style={{ fontSize: 11.5 }}>
                      {(c.services || []).map((s) => s.name).join(', ') || '—'}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm btn-primary"
                              disabled={!!restoring[c.id] || !!purging[c.id]}
                              onClick={() => setConfirmTarget(c)}>
                        <I.Restart size={12}/> {restoring[c.id] ? 'Restoring…' : 'Restore'}
                      </button>
                      <button className="btn btn-sm btn-danger" style={{ marginLeft: 6 }}
                              disabled={!!restoring[c.id] || !!purging[c.id]}
                              onClick={() => { setPurgeConfirm(''); setPurgeTarget(c); }}>
                        <I.Trash size={12}/> {purging[c.id] ? 'Deleting…' : 'Delete forever'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={confirmTarget !== null} onClose={() => setConfirmTarget(null)}
             title={confirmTarget ? `Restore tenant "${confirmTarget.displayName || confirmTarget.id}"?` : ''}
             subtitle="The BE will re-render the tenant into git and ArgoCD will pick it back up. Existing data in the namespace (if not yet purged) stays; new pods come up fresh."
             footer={
               <>
                 <button className="btn" onClick={() => setConfirmTarget(null)}>Cancel</button>
                 <button className="btn btn-primary" onClick={doRestore}>
                   <I.Restart size={13}/> Restore
                 </button>
               </>
             }>
        {confirmTarget && (
          <div className="col" style={{ gap: 10 }}>
            <KVrow label="Client ID" value={<span className="mono">{confirmTarget.id}</span>}/>
            <KVrow label="Plan" value={<span className="badge">{confirmTarget.plan}</span>}/>
            <KVrow label="Deleted"
                   value={<span className="muted">{confirmTarget.deletedAt ? relativeTime(confirmTarget.deletedAt) : '—'}</span>}/>
            {confirmTarget.purgeAt && (() => {
              const left = daysLeft(confirmTarget.purgeAt);
              return (
                <KVrow label="Auto-purge in"
                       value={
                         <span className={'badge ' + (left != null && left <= 7 ? 'badge-red' : 'badge-amber')}>
                           {left == null ? '—' : left === 0 ? 'any moment' : `${left} day${left === 1 ? '' : 's'}`}
                         </span>
                       }/>
              );
            })()}
            <KVrow label="Services"
                   value={
                     <span className="mono" style={{ fontSize: 12 }}>
                       {(confirmTarget.services || []).map((s) => s.name).join(', ') || '—'}
                     </span>
                   }/>
          </div>
        )}
      </Modal>

      <Modal open={purgeTarget !== null} onClose={() => { setPurgeTarget(null); setPurgeConfirm(''); }}
             title={purgeTarget ? `Permanently delete "${purgeTarget.displayName || purgeTarget.id}"?` : ''}
             subtitle="This skips the 30-day recovery window and deletes the tenant for good. The database row (and its services) are removed and the cluster/git cleanup is re-run. This cannot be undone."
             footer={
               <>
                 <button className="btn" onClick={() => { setPurgeTarget(null); setPurgeConfirm(''); }}>Cancel</button>
                 <button className="btn btn-danger-solid"
                         disabled={!purgeTarget || purgeConfirm.trim() !== purgeTarget.id}
                         onClick={doPurge}>
                   <I.Trash size={13}/> Delete permanently
                 </button>
               </>
             }>
        {purgeTarget && (
          <div className="col" style={{ gap: 10 }}>
            <div className="field-error" style={{ fontSize: 12.5 }}>
              Irreversible — there is no trash to restore from after this.
            </div>
            <KVrow label="Client ID" value={<span className="mono">{purgeTarget.id}</span>}/>
            <KVrow label="Services"
                   value={
                     <span className="mono" style={{ fontSize: 12 }}>
                       {(purgeTarget.services || []).map((s) => s.name).join(', ') || '—'}
                     </span>
                   }/>
            <div className="field">
              <label className="field-label">Type <span className="mono">{purgeTarget.id}</span> to confirm</label>
              <input className="input input-mono" autoFocus value={purgeConfirm}
                     placeholder={purgeTarget.id}
                     onChange={(e) => setPurgeConfirm(e.target.value)}/>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={purgeAllOpen} onClose={() => { setPurgeAllOpen(false); setPurgeAllConfirm(''); }}
             title={`Permanently delete all ${items.length} tenant${items.length === 1 ? '' : 's'} in Trash?`}
             subtitle="Every soft-deleted tenant is purged for good — DB rows, services, and the cluster/git cleanup. This skips the recovery window and cannot be undone."
             footer={
               <>
                 <button className="btn" onClick={() => { setPurgeAllOpen(false); setPurgeAllConfirm(''); }}>Cancel</button>
                 <button className="btn btn-danger-solid"
                         disabled={purgeAllConfirm.trim().toLowerCase() !== 'delete all'}
                         onClick={doPurgeAll}>
                   <I.Trash size={13}/> Delete all permanently
                 </button>
               </>
             }>
        <div className="col" style={{ gap: 10 }}>
          <div className="field-error" style={{ fontSize: 12.5 }}>
            Irreversible — this empties the Trash. There is nothing to restore from afterwards.
          </div>
          <div className="field">
            <label className="field-label">Type <span className="mono">delete all</span> to confirm</label>
            <input className="input input-mono" autoFocus value={purgeAllConfirm}
                   placeholder="delete all"
                   onChange={(e) => setPurgeAllConfirm(e.target.value)}/>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// relativeTime renders an RFC3339 timestamp as "Xs ago", "Xm ago", etc.
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const dSec = Math.max(0, (Date.now() - t) / 1000);
  if (dSec < 60) return `${Math.round(dSec)}s ago`;
  if (dSec < 3600) return `${Math.round(dSec / 60)}m ago`;
  if (dSec < 86400) return `${Math.round(dSec / 3600)}h ago`;
  if (dSec < 30 * 86400) return `${Math.round(dSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ─── AUDIT LOG ──────────────────────────────────────────────────────────────
export function AuditLog() {
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [fromFilter, setFromFilter] = useState('');
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // BE-side filter for action/target/from — q + actor stay client-side.
  const reload = () => {
    setLoading(true);
    setErr(null);
    fetchAudit({
      limit: 200,
      action: actionFilter || undefined,
      target: targetFilter || undefined,
      from: fromFilter ? new Date(fromFilter).toISOString() : undefined,
    })
      .then((page) => { setEvents(page.items); setTotal(page.total); })
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = events.filter((e) => {
    if (filter !== 'all' && e.actorEmail !== filter) return false;
    if (q && !(e.target.toLowerCase().includes(q.toLowerCase()) || e.action.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const actors = ['all', ...Array.from(new Set(events.map((e) => e.actorEmail)))];
  const allActions = ['', ...Array.from(new Set(events.map((e) => e.action))).sort()];

  const exportCSV = () => {
    const head = 'time,actor,actor_type,action,target,request_id,payload';
    const rows = filtered.map((e) => {
      const cells = [
        e.createdAt,
        e.actorEmail,
        e.actorType,
        e.action,
        e.target,
        e.requestId ?? '',
        e.payload ? JSON.stringify(e.payload).replace(/"/g, '""') : '',
      ];
      return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');
    });
    const blob = new Blob([head + '\n' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `audit-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="page-narrow">
      <div className="page-head">
        <h1 className="page-title">Audit log</h1>
        <p className="page-sub">Every change to the cluster, by user and time. Retained for 90 days.</p>
      </div>
      <div className="card">
        <div className="card-head">
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', width: 220 }}>
              <I.Search size={13} style={{ position: 'absolute', left: 9, top: 7, color: 'var(--text-3)' }}/>
              <input className="input" placeholder="Quick filter (client)…"
                     style={{ paddingLeft: 30, height: 28, fontSize: 12 }}
                     value={q} onChange={(e) => setQ(e.target.value)}/>
            </div>
            <select className="select" style={{ height: 28, width: 'auto', paddingRight: 26, fontSize: 12 }}
                    value={filter} onChange={(e) => setFilter(e.target.value)}>
              {actors.map((a) => <option key={a} value={a}>{a === 'all' ? 'All actors' : a}</option>)}
            </select>
            <select className="select" style={{ height: 28, width: 'auto', paddingRight: 26, fontSize: 12 }}
                    value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); }}>
              {allActions.map((a) => <option key={a || '_'} value={a}>{a || 'Any action'}</option>)}
            </select>
            <input className="input input-mono" placeholder="Target prefix, e.g. clients/atlas-prints"
                   style={{ height: 28, fontSize: 12, width: 240 }}
                   value={targetFilter} onChange={(e) => setTargetFilter(e.target.value)}/>
            <input className="input" type="datetime-local"
                   style={{ height: 28, fontSize: 12 }}
                   value={fromFilter} onChange={(e) => setFromFilter(e.target.value)}/>
            <button className="btn btn-sm" onClick={reload}><I.Refresh size={12}/> Apply</button>
            <button className="btn btn-sm" onClick={exportCSV} disabled={filtered.length === 0}>
              <I.File size={12}/> Export CSV
            </button>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>
            {loading ? 'Loading…' : `${filtered.length} shown · ${total} total`}
          </span>
        </div>
        {err && <div className="field-error" style={{ padding: 12 }}>Failed to load audit log: {err}</div>}
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 160 }}>Time</th>
              <th style={{ width: 180 }}>Actor</th>
              <th style={{ width: 180 }}>Action</th>
              <th>Target</th>
              <th>Payload</th>
            </tr>
          </thead>
          <tbody>
            {!loading && !err && filtered.length === 0 && (
              <tr>
                <td colSpan={5}>
                  <div className="empty" style={{ padding: '28px 0' }}>No matching events.</div>
                </td>
              </tr>
            )}
            {filtered.map((e) => (
              <tr key={e.id}>
                <td className="muted mono" style={{ fontSize: 12 }}>{relativeTime(e.createdAt)}</td>
                <td>
                  <span className="badge" style={e.actorType === 'admin' ? {} : { background: 'var(--blue-bg)', color: 'var(--blue)' }}>
                    {e.actorEmail}
                  </span>
                </td>
                <td><span className="mono" style={{ fontSize: 12 }}>{e.action}</span></td>
                <td><span className="mono" style={{ fontSize: 12, color: 'var(--text-1)' }}>{e.target}</span></td>
                <td className="muted mono" style={{ fontSize: 11.5, maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.payload ? JSON.stringify(e.payload) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── SETTINGS ───────────────────────────────────────────────────────────────
export function Settings({
  state, setState, theme, setTheme, accent, setAccent,
}: {
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppState>>;
  theme: string;
  setTheme: (v: string) => void;
  accent: string;
  setAccent: (v: string) => void;
}) {
  const toast = useToast();
  const { unlocked: operator } = useOperatorMode();
  const [draft, setDraft] = useState(state.settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(state.settings), [state.settings]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(state.settings);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateSettings(draft);
      setState((s) => ({ ...s, settings: { ...s.settings, ...updated } }));
      toast?.success('Settings saved');
    } catch (err) {
      toast?.error('Save failed', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-narrow" style={{ maxWidth: 880 }}>
      <div className="page-head">
        <h1 className="page-title">Settings</h1>
        <p className="page-sub">Cluster-wide configuration for the master admin console.</p>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <div><h3>Profile</h3><p>Signed in as master administrator</p></div>
        </div>
        <div className="card-pad row" style={{ gap: 16 }}>
          <Avatar name={state.auth?.name || 'Master Admin'} size={48}/>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500 }}>{state.auth?.name}</div>
            <div className="muted" style={{ fontSize: 12.5 }}>{state.auth?.email}</div>
            <div className="row" style={{ gap: 8, marginTop: 8 }}>
              <span className="badge badge-violet">role: master-admin</span>
              <span className="badge">password auth</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <div><h3>Provisioning defaults</h3><p>Used when a new client is created</p></div>
        </div>
        <div className="card-pad res-grid">
          <div className="field">
            <label className="field-label">Base repository</label>
            <input className="input input-mono" value={draft.baseRepo} onChange={(e) => setDraft({ ...draft, baseRepo: e.target.value })}/>
            <div className="field-hint">Forked for each new client to bootstrap their workloads</div>
          </div>
          <div className="field">
            <label className="field-label">Base branch</label>
            <input className="input input-mono" value={draft.baseBranch} onChange={(e) => setDraft({ ...draft, baseBranch: e.target.value })}/>
          </div>
          <div className="field">
            <label className="field-label">ArgoCD server</label>
            <input className="input input-mono" value={draft.argoServer} onChange={(e) => setDraft({ ...draft, argoServer: e.target.value })}/>
          </div>
          <div className="field">
            <label className="field-label">Container registry</label>
            <input className="input input-mono" value={draft.registry} onChange={(e) => setDraft({ ...draft, registry: e.target.value })}/>
          </div>
          <div className="field">
            <label className="field-label">Default plan</label>
            <select className="select" value={draft.defaultPlan} onChange={(e) => setDraft({ ...draft, defaultPlan: e.target.value as Plan })}>
              {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn" disabled={!dirty || saving} onClick={() => setDraft(state.settings)}>Reset</button>
          <button className="btn btn-primary" disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <div><h3>Appearance</h3><p>Personal preferences (saved to this browser)</p></div>
        </div>
        <div className="card-pad col" style={{ gap: 16 }}>
          <div className="spread">
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>Theme</div>
              <div className="muted" style={{ fontSize: 12 }}>Dark or light interface</div>
            </div>
            <div className="row" style={{ gap: 6 }}>
              <button className={'btn btn-sm ' + (theme === 'dark' ? 'btn-primary' : '')} onClick={() => setTheme('dark')}>
                <I.Moon size={12}/> Dark
              </button>
              <button className={'btn btn-sm ' + (theme === 'light' ? 'btn-primary' : '')} onClick={() => setTheme('light')}>
                <I.Sun size={12}/> Light
              </button>
            </div>
          </div>
          <div className="spread">
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>Accent color</div>
              <div className="muted" style={{ fontSize: 12 }}>Used for highlights, buttons and links</div>
            </div>
            <div className="row" style={{ gap: 6 }}>
              {[
                { id: 'indigo',  color: '#6366f1', name: 'Indigo' },
                { id: 'emerald', color: '#10b981', name: 'Emerald' },
                { id: 'amber',   color: '#f59e0b', name: 'Amber' },
              ].map((a) => (
                <button key={a.id}
                        className={'btn btn-sm'}
                        onClick={() => setAccent(a.id)}
                        style={accent === a.id ? { borderColor: a.color, color: a.color } : {}}>
                  <span style={{ width: 10, height: 10, borderRadius: 999, background: a.color, display: 'inline-block' }}/>
                  {a.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <PlanLimitsPanel/>
      <ChartCatalogPanel/>
    </div>
  );
}

// ─── PLAN LIMITS EDITOR ────────────────────────────────────────────────────
const GiB = 1 << 30;

function fmtMem(bytes: number): string {
  if (bytes >= GiB) return `${(bytes / GiB).toFixed(bytes % GiB === 0 ? 0 : 1)} GB`;
  return `${(bytes / (1 << 20)).toFixed(0)} MB`;
}

function fmtBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(1)} TB`;
  if (bytes >= GiB) return `${(bytes / GiB).toFixed(1)} GB`;
  return `${(bytes / (1 << 20)).toFixed(0)} MB`;
}

function fmtCPU(milli: number): string {
  return milli >= 1000 ? `${(milli / 1000).toFixed(milli % 1000 === 0 ? 0 : 1)} cores` : `${milli}m`;
}

// Edit state uses human-friendly units: GB and cores (floats).
type PlanDraft = { replicas: number; cpuCores: number; memGB: number; gpuCpuCores: number; gpuMemGB: number };

function toDraft(l: PlanLimits): PlanDraft {
  return {
    replicas: l.maxReplicas,
    cpuCores: l.maxMilliCPU / 1000,
    memGB: l.maxMemoryBytes / GiB,
    gpuCpuCores: l.maxGPUMilliCPU / 1000,
    gpuMemGB: l.maxGPUMemoryBytes / GiB,
  };
}

function PlanLimitsPanel() {
  const toast = useToast();
  const [limits, setLimits] = useState<PlanLimits[]>([]);
  const [selected, setSelected] = useState<string>('starter');
  const [drafts, setDrafts] = useState<Record<string, PlanDraft>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPlanLimits().then((ls) => {
      setLimits(ls);
      const d: Record<string, PlanDraft> = {};
      ls.forEach((l) => { d[l.plan] = toDraft(l); });
      setDrafts(d);
    }).catch(() => {});
  }, []);

  const draft = drafts[selected];
  const original = limits.find((l) => l.plan === selected);
  const dirty = draft && original && JSON.stringify(draft) !== JSON.stringify(toDraft(original));

  const setField = (field: keyof PlanDraft, val: number) =>
    setDrafts((prev) => ({ ...prev, [selected]: { ...prev[selected], [field]: val } }));

  const reset = () => {
    if (!original) return;
    setDrafts((prev) => ({ ...prev, [selected]: toDraft(original) }));
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const updated = await updatePlanLimits(selected, {
        maxReplicas:       draft.replicas,
        maxMilliCPU:       Math.round(draft.cpuCores * 1000),
        maxMemoryBytes:    Math.round(draft.memGB * GiB),
        maxGPUMilliCPU:    Math.round(draft.gpuCpuCores * 1000),
        maxGPUMemoryBytes: Math.round(draft.gpuMemGB * GiB),
      });
      setLimits((prev) => prev.map((l) => l.plan === selected ? updated : l));
      setDrafts((prev) => ({ ...prev, [selected]: toDraft(updated) }));
      toast?.success(`Plan "${selected}" updated`);
    } catch (err) {
      toast?.error('Save failed', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const numField = (label: string, field: keyof PlanDraft, suffix: string, hint?: string) => (
    <div className="field">
      <label className="field-label">{label}</label>
      <div className="input-row">
        <input type="number" className="input" step="any" min={0.001}
          value={draft ? (draft[field] as number) : ''}
          onChange={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) setField(field, v); }}/>
        <span className="input-suffix">{suffix}</span>
      </div>
      {hint && <div className="field-hint">{hint}</div>}
    </div>
  );

  const sectionLabel = (text: string, sub?: string) => (
    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-3)', marginBottom: 8 }}>
      {text}{sub && <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 5, color: 'var(--text-3)' }}>{sub}</span>}
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-head">
        <div><h3>Plan limits</h3><p>Per-service resource caps enforced when editing a service</p></div>
      </div>

      <div style={{ display: 'flex', borderTop: '1px solid var(--border)' }}>
        {/* Left: plan list */}
        <div style={{ width: 130, borderRight: '1px solid var(--border)', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          {KnownPlans.map((p) => (
            <button key={p} onClick={() => setSelected(p)} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', padding: '0 14px', fontSize: 13,
              fontWeight: selected === p ? 600 : 400,
              background: selected === p ? 'var(--bg-2)' : 'transparent',
              color: selected === p ? 'var(--text-1)' : 'var(--text-2)',
              border: 'none', borderBottom: '1px solid var(--border)',
              cursor: 'pointer', textAlign: 'left',
            }}>
              {p}
              {selected === p && <span style={{ color: 'var(--accent)', fontSize: 16, lineHeight: 1 }}>›</span>}
            </button>
          ))}
        </div>

        {/* Right pane: the editable fields always establish the pane height (so
            the plan list never resizes between plans). On the unlimited tier we
            hide those fields and overlay a centered notice at the same height —
            no empty gap, no shrinking sidebar. */}
        <div style={{ flex: 1, position: 'relative' }}>
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14,
                        visibility: selected === 'unlimited' ? 'hidden' : undefined }}>
            {sectionLabel('Standard')}
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 10, alignItems: 'start' }}>
              {numField('Max replicas', 'replicas', 'pods')}
              {numField('CPU', 'cpuCores', 'cores')}
              {numField('Memory', 'memGB', 'GB')}
            </div>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              {sectionLabel('GPU services', '— higher caps for GPU workloads')}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, alignItems: 'start' }}>
                {numField('CPU', 'gpuCpuCores', 'cores')}
                {numField('Memory', 'gpuMemGB', 'GB')}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 2 }}>
              <button className="btn btn-sm" disabled={!dirty} onClick={reset}>Reset</button>
              <button className="btn btn-sm btn-primary" disabled={!dirty || saving} onClick={save}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
          {selected === 'unlimited' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 8,
                          alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 20 }}>
              <div style={{ fontSize: 26, fontWeight: 700 }}>∞ Unlimited</div>
              <div className="muted" style={{ fontSize: 13, maxWidth: 300 }}>No resource caps — services may use any replicas, CPU, memory and GPU.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── GITOPS CHART CATALOG ──────────────────────────────────────────────────
// Operator-facing button to hot-reload the chart catalog from the GitOps
// repo. Calls POST /api/v1/admin/reload-charts. After pushing a new
// chart-meta.yaml the admin clicks Reload and the picker updates within
// seconds — no BE restart.
function ChartCatalogPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof reloadCharts>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();

  const onReload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await reloadCharts();
      setResult(r);
      if ((r.errors || []).length > 0) {
        toast?.error(`Reload finished with ${r.errors!.length} parse error(s) — see details below`);
      } else {
        toast?.success(`Catalog reloaded — ${r.loaded} chart${r.loaded === 1 ? '' : 's'}`);
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : String(e);
      setErr(msg);
      toast?.error(`Reload failed: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Chart catalog</h3>
          <p>Discover charts added to the GitOps repo without restarting the backend.</p>
        </div>
      </div>
      <div className="card-pad col" style={{ gap: 12 }}>
        <div className="spread" style={{ alignItems: 'flex-start' }}>
          <div style={{ maxWidth: 500 }}>
            <div style={{ fontWeight: 500, fontSize: 13 }}>Reload from GitOps</div>
            <div className="muted" style={{ fontSize: 12.5 }}>
              BE pulls the GitOps repo, re-scans <span className="mono">charts/*/chart-meta.yaml</span>,
              and swaps the live catalog. Use after pushing a new chart so it appears in the Add-service picker.
            </div>
          </div>
          <button className="btn btn-primary" disabled={loading} onClick={onReload}>
            <I.Refresh size={13}/> {loading ? 'Reloading…' : 'Reload catalog'}
          </button>
        </div>

        {err && <div className="field-error" style={{ padding: 10 }}>Reload failed: {err}</div>}

        {result && (
          <div className="card" style={{ padding: 12, background: 'var(--bg-2)' }}>
            <div className="row" style={{ gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <span className="badge badge-green"><span className="dot"></span>{result.loaded} chart{result.loaded === 1 ? '' : 's'}</span>
              {result.gitPulled
                ? <span className="badge"><I.GitBranch size={10}/> git pulled</span>
                : <span className="badge badge-amber">no git pull (offline / disabled)</span>}
              {(result.errors || []).length > 0 && (
                <span className="badge badge-red">{result.errors!.length} parse error{result.errors!.length === 1 ? '' : 's'}</span>
              )}
            </div>
            <div style={{ fontSize: 12.5 }}>
              <span className="muted">Services: </span>
              {result.services.length === 0
                ? <span className="muted">none</span>
                : result.services.map((s) => (
                    <span key={s} className="mono" style={{ marginRight: 8 }}>{s}</span>
                  ))}
            </div>
            {(result.errors || []).length > 0 && (
              <div className="col" style={{ gap: 6, marginTop: 10 }}>
                {result.errors!.map((e, i) => (
                  <div key={i} style={{ padding: 8, background: 'var(--red-bg)', borderRadius: 6, fontSize: 12 }}>
                    <div className="mono" style={{ color: 'var(--red)', fontSize: 11 }}>{e.path}</div>
                    <div style={{ marginTop: 2 }}>{e.error}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
